import { Button, ExpandableSection, Table, TableSelection } from 'AWS-UI-Components-React';
import React from 'react';
import { ASIN_METRIC_TYPE, COMPONENT_NAMES, DEFAULT_TREND_WEEKS } from '../../../constants/asin_metric_elements';
import { CHANNELS } from '../../../constants/channels';
import { formatTimeFrameToYYYYMM } from '../../../utils/date-utils';
import logger from '../../../utils/logging';
import { EmptyMessage } from '../../content/empty_message';
import { ErrorFlash } from '../../content/error_flash';
import { OPERATION_STATUS, TEXT } from '../common/constants';
import { AsinMetricModal } from './asin_metric_modal';
import { getAsinMetricsClient } from './asin_metrics_client';
import {
  generateGlOrAsinMetricsReportRows,
  getColumnDefinitions,
  getTrendGraphSnapshotMap,
  mergeKeysOfObjectsWithSameId,
} from './asin_metrics_utils';

export class AsinInfoTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      asinMetricsLoadStatus: OPERATION_STATUS.NONE,
      asinMetricRows: [],
      asinModalVisible: false,
      trendDataRows: [],
      trendLines: [],
      asin: "",
      gl: -1,
      availableMonths: [formatTimeFrameToYYYYMM(this.props.payload.timeFrame).toString()],
      snapshotToComponentMetricMap: null,
      latestSnapshot: 0,
      emptyAsinSelection: true,
    };
  }

  componentDidMount = () => {
    this.initialClientTasks();
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if(prevProps.items !== this.props.items){
      this.setState({emptyAsinSelection: true});
    }
  }

  initialClientTasks = () => {
    this.asinMetricsClient = getAsinMetricsClient();
  };

  pullAsinMetrics = (asin, gl) => {
    //Backend only accepts Retail not RETAIL
    const channel = CHANNELS.RETAIL === this.props.payload.channel ? 'Retail' : this.props.payload.channel;
    const payload = {marketplaceId: this.props.payload.marketplace, channel, components: [this.props.component],
      month:formatTimeFrameToYYYYMM(this.props.payload.timeFrame), gl, asin, trendPeriodInWeeks: DEFAULT_TREND_WEEKS,
      metrics: [ASIN_METRIC_TYPE.NET_FA, ASIN_METRIC_TYPE.NET_LA,
                ASIN_METRIC_TYPE.NET_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT]};
    this.setState({asinMetricsLoadStatus: OPERATION_STATUS.IN_PROGRESS});
    this.asinMetricsClient.getAsinMetrics(this.processAsinMetrics, this.onFailedRequest, payload);
  }

  processAsinMetrics = (response) => {
    if (response.data && response.data.asinMetricItems && response.data.asinMetricItems.length) {
      const asinMetricItems = response.data.asinMetricItems;
      this.processTrendGraphData(asinMetricItems);
      const [reportRows, snapshotToComponentMetricMap, latestSnapshot] = generateGlOrAsinMetricsReportRows(asinMetricItems);
      this.setState({asinMetricRows:reportRows, asinMetricsLoadStatus: OPERATION_STATUS.SUCESS,
                    snapshotToComponentMetricMap, latestSnapshot});
    } else {
      logger.info('Received incomplete response from /asinMetrics api.');
      this.setState({asinMetricsLoadStatus: OPERATION_STATUS.NONE});
    }
  }

  processTrendGraphData = (asinMetricItems) => {
    const snapshotMap = getTrendGraphSnapshotMap(asinMetricItems);
    const trendDataRows = [];
    let uniqueTrendLines = new Set();
    for(const key of snapshotMap.keys()){
      trendDataRows.push(snapshotMap.get(key));
      Object.keys(snapshotMap.get(key)).filter(k => k !== 'snapshot').forEach(lineId => {
        uniqueTrendLines.add(lineId);
      });
    }
    //Merge keys of trendDataRow with the same snapshotDate
    const combinedRows = mergeKeysOfObjectsWithSameId(trendDataRows, 'snapshot');
    this.setState({trendDataRows: combinedRows, trendLines: [...uniqueTrendLines]});
  }

  onFailedRequest = () => {
    this.setState({asinMetricsLoadStatus: OPERATION_STATUS.FAIL});
  };

  onCloseAsinMetricModel = () => {
    this.setState({asinModalVisible: false});
  }

  onViewAsinDetail = () => {
    this.setState({loadingAsinMetrics: true, asinModalVisible: true});
    this.pullAsinMetrics(this.state.asin, this.state.gl);
  }

  onAsinSelectionChange = (event) => {
    if (event.detail.selectedItems && event.detail.selectedItems.length > 0){
      const item = event.detail.selectedItems[0];
      this.setState({gl: item.gl, asin: item.asin, emptyAsinSelection: false});
    }
  }

  render() {
    const {tableName, items, loadStatus, displayInMillions, component} = this.props;
    const componentName = component && component.length ? COMPONENT_NAMES[component] : '';
    const emptyAsinInfo = !items || !items.length;

    return (
      <div>
        {loadStatus === OPERATION_STATUS.FAIL && (<ErrorFlash type="ASIN Info" />)}
        <ExpandableSection header={`${tableName}: ${componentName}`}
                           expanded={loadStatus === OPERATION_STATUS.IN_PROGRESS || items.length > 0}>
          <Table
            className="ZebraStripesTable accuracy-table-container"
            columnDefinitions={getColumnDefinitions(items, this.props.displayInMillions)}
            items={items}
            loadingText={TEXT.LOADING}
            loading={loadStatus === OPERATION_STATUS.IN_PROGRESS}
            stickyHeader={true}
            wrapLines={false}
            empty={<EmptyMessage />}
            header={
              <div className="awsui-util-action-stripe">
                <div className="awsui-util-action-stripe-title">
                </div>
                <div className="awsui-util-action-stripe-group">
                  <Button
                    text="View ASIN Details"
                    variant="primary"
                    onClick={this.onViewAsinDetail}
                    disabled={loadStatus === OPERATION_STATUS.IN_PROGRESS || emptyAsinInfo
                    || this.state.emptyAsinSelection}
                    loading={this.state.downloadStatus === OPERATION_STATUS.IN_PROGRESS}
                  />
                </div>
              </div>
            }
          >
            <TableSelection selectionType='single' trackBy='asin' onSelectionChange={this.onAsinSelectionChange}/>
          </Table>
        </ExpandableSection>
        {this.state.asinModalVisible && <AsinMetricModal items={this.state.asinMetricRows} loadStatus={this.state.asinMetricsLoadStatus}
                                                         displayInMillions={displayInMillions} onClose={this.onCloseAsinMetricModel}
                                                         asin={this.state.asin} component={this.props.component}
                                                         gl={this.state.gl}
                                                         trendDataRows={this.state.trendDataRows}
                                                         snapshotToComponentMetricMap={this.state.snapshotToComponentMetricMap}
                                                         latestSnapshot={this.state.latestSnapshot}
                                                         trendLines={this.state.trendLines}
                                                         availableMonths={this.state.availableMonths}
                                                         payload={this.props.payload} />}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/asin_info_table.jsx